import type { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { ClockIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useRouteLoaderData } from '@remix-run/react';
import { PLAN_LOGOS } from '@shared/shared-constants/plan-logos';
import cn from 'classnames';
import type { TFunction } from 'i18next';

import type { CommunityPostHistoryUpsell, CommunityPostHistoryUpsellPlan } from '~/interfaces/community';
import type { loader as communityLoader } from '~/routes/community';

import { PillLink } from '../../ui/PillLink';

// The dashboard's upgrade modal and its tracking know this prompt by this location.
const UPSELL_LOCATION = 'community_feed';

// Sits above the first post members can't see; the server marks those posts only for admins and managers.
export const HiddenFromMembersNotice: FC = () => {
  const { t } = useTranslation();
  const layoutData = useRouteLoaderData<typeof communityLoader>('routes/community');
  const community = layoutData?.community;
  const days = community?.post_history_days;
  const upsell = community?.post_history_upsell;

  if (!days) return null;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <p className="flex items-center gap-1.5 text-xs text-black/60">
          <EyeSlashIcon className="size-3.5" aria-hidden="true" />
          <span>{t('community.feed.hidden_from_members.staff_only')}</span>
        </p>
        <section className="flex flex-col gap-4 rounded-3xl border border-black/10 p-4">
          <div className={cn('flex gap-4', { 'items-start': upsell, 'items-center': !upsell })}>
            <span className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-black/5">
              <ClockIcon className="size-5 text-black/70" aria-hidden="true" />
            </span>
            <div className="flex flex-col gap-1">
              <p className="text-base font-semibold leading-6 text-black">
                {t('community.feed.hidden_from_members.title', { days })}
              </p>
              {upsell && <p className="text-sm leading-5 text-black/60">{upsellDescription(upsell, days, t)}</p>}
            </div>
          </div>
          {upsell && <UnlockPlanLink plan={upsell.plans[0]} upgradeUrl={upsell.upgrade_url} />}
        </section>
      </div>
      <div className="flex items-center gap-3 text-xs text-black/50">
        <span className="h-px flex-1 bg-black/10" aria-hidden="true" />
        <span>{t('community.feed.hidden_from_members.separator')}</span>
        <span className="h-px flex-1 bg-black/10" aria-hidden="true" />
      </div>
    </div>
  );
};

interface UnlockPlanLinkProps {
  plan: CommunityPostHistoryUpsellPlan;
  upgradeUrl: string;
}

const UnlockPlanLink: FC<UnlockPlanLinkProps> = ({ plan, upgradeUrl }) => {
  const url = new URL(upgradeUrl);
  url.searchParams.set('upsell', UPSELL_LOCATION);

  return (
    <PillLink to={url.toString()} target="_blank" rel="noopener noreferrer" variant="secondary" className="self-start">
      <span className="inline-flex items-center gap-1.5">
        <Trans
          i18nKey="community.feed.hidden_from_members.unlock"
          values={{ plan: plan.name }}
          components={{
            logo: <img key="logo" src={PLAN_LOGOS[plan.id]} alt="" className="size-4" />,
            plan: <span key="plan" className="text-violet-600" />,
          }}
        />
      </span>
    </PillLink>
  );
};

const upsellDescription = (
  { current_plan_name: currentPlanName, plans }: CommunityPostHistoryUpsell,
  days: number,
  t: TFunction,
) => {
  const current = t('community.feed.hidden_from_members.current_plan', { plan: currentPlanName, days });
  const options = plans.map((plan) => upgradeOption(plan, t));

  return `${current} ${t('community.feed.hidden_from_members.upgrade', { options })}`;
};

const upgradeOption = (plan: CommunityPostHistoryUpsellPlan, t: TFunction) => {
  if (plan.days === null) {
    return t('community.feed.hidden_from_members.option_everything', { plan: plan.name });
  }

  return t('community.feed.hidden_from_members.option_days', { plan: plan.name, days: plan.days });
};
